
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type ContactCategory = "Allgemeines" | "Probetraining" | "Mitgliedschaft";

export type ContactOverlayDefaults = {
  category?: ContactCategory;
};

type ContactOverlayContextValue = {
  isOpen: boolean;
  defaults: Required<ContactOverlayDefaults>;
  openContact: (defaults?: ContactOverlayDefaults) => void;
  closeContact: () => void;
};

const ContactOverlayContext = createContext<ContactOverlayContextValue | null>(null);

const DEFAULTS: Required<ContactOverlayDefaults> = {
  category: "Allgemeines",
};

export function ContactOverlayProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [defaults, setDefaults] = useState<Required<ContactOverlayDefaults>>(DEFAULTS);

  const openContact = useCallback((next?: ContactOverlayDefaults) => {
    setDefaults({
      category: next?.category ?? DEFAULTS.category,
    });
    setIsOpen(true);
  }, []);

  const closeContact = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    const openFromHash = () => {
      if (window.location.hash === "#kontakt") {
        openContact({ category: "Allgemeines" });
      }
    };

    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, [openContact]);

  const value = useMemo(
    () => ({
      isOpen,
      defaults,
      openContact,
      closeContact,
    }),
    [isOpen, defaults, openContact, closeContact]
  );

  return <ContactOverlayContext.Provider value={value}>{children}</ContactOverlayContext.Provider>;
}

export function useContactOverlay() {
  const ctx = useContext(ContactOverlayContext);
  if (!ctx) {
    throw new Error("useContactOverlay must be used within ContactOverlayProvider");
  }
  return ctx;
}
